import type { EChartsOption } from 'echarts';
import { useMemo } from 'react';

import { seriesColor, useTheme } from '../../theme/readTheme';
import type { ChartData, ChartFields } from '../../types';
import { EChart } from '../EChart';
import { Plot } from '../Plot';
import { Empty } from '../states';
import { cell, humanise, isNumeric } from '../format';
import { animation, tooltipChrome } from '../options';

/**
 * Part-to-whole where the parts are many — a treemap packs a category's share
 * into a rectangle whose area is its value, so fifty segments read at a glance
 * where a pie would collapse into unreadable slivers.
 *
 * Expects a label column and a numeric column: `[name, value]`. A second
 * categorical column, if present, becomes the parent grouping (one level of
 * hierarchy) — the treemap's reason to exist over a bar chart.
 *
 * Colour is by top-level group, assigned in palette order, so identity follows
 * the group rather than the magnitude.
 */

interface Props {
  data: ChartData;
  /** Author's column choices; absent means infer. */
  fields?: ChartFields;
}

interface Node {
  name: string;
  value: number;
  children?: Node[];
  itemStyle?: { color: string };
}

const treemapView = (data: ChartData, theme: ReturnType<typeof useTheme>) => {
  if (!data.columns.length || !data.rows.length) return null;

  const numericAt = (i: number) => data.rows.slice(0, 30).some((r) => isNumeric(r[i]));
  const measureIdx = data.columns.findIndex((_, i) => numericAt(i));
  const dims = data.columns.map((_, i) => i).filter((i) => !numericAt(i));
  if (measureIdx < 0 || dims.length < 1) return null;

  const text = (v: unknown) => String(v ?? '');
  const labelIdx = dims[0];
  const groupIdx = dims.length > 1 ? dims[1] : -1;
  const measureName = humanise(data.columns[measureIdx]);

  let nodes: Node[];
  if (groupIdx >= 0) {
    // One level of hierarchy: group → leaf.
    const groups = new Map<string, Node>();
    for (const r of data.rows) {
      if (!isNumeric(r[measureIdx])) continue;
      const g = text(r[groupIdx]) || '—';
      let parent = groups.get(g);
      if (!parent) {
        parent = { name: g, value: 0, children: [] };
        groups.set(g, parent);
      }
      parent.children!.push({ name: text(r[labelIdx]), value: r[measureIdx] as number });
    }
    nodes = [...groups.values()].map((g, i) => ({
      ...g,
      itemStyle: { color: seriesColor(theme, i) },
    }));
  } else {
    nodes = data.rows
      .filter((r) => isNumeric(r[measureIdx]))
      .map((r, i) => ({
        name: text(r[labelIdx]),
        value: r[measureIdx] as number,
        itemStyle: { color: seriesColor(theme, i % 8) },
      }));
  }

  if (!nodes.length) return null;

  const option: EChartsOption = {
    ...animation,
    tooltip: {
      trigger: 'item',
      ...tooltipChrome(theme),
      formatter: (params: unknown) => {
        const p = params as { name: string; value: number };
        const root = document.createElement('div');
        root.className = 'chart-tip';
        const head = document.createElement('div');
        head.className = 'chart-tip__title';
        head.textContent = p.name;
        const row = document.createElement('div');
        row.className = 'chart-tip__row';
        const n = document.createElement('span');
        n.className = 'chart-tip__name';
        n.textContent = measureName;
        const v = document.createElement('strong');
        v.className = 'chart-tip__value';
        v.textContent = cell(p.value, 'exact');
        row.append(n, v);
        root.append(head, row);
        return root;
      },
    },
    series: [
      {
        type: 'treemap' as const,
        data: nodes,
        roam: false,
        nodeClick: false,
        breadcrumb: { show: false },
        width: '100%',
        height: '100%',
        top: 2,
        left: 2,
        right: 2,
        bottom: 2,
        itemStyle: { borderColor: theme.surface, borderWidth: 2, gapWidth: 2 },
        label: {
          show: true,
          color: '#fff',
          fontSize: 12,
          fontFamily: theme.font,
          overflow: 'truncate' as const,
          formatter: (p: { name?: string; value?: unknown }) => `${p.name ?? ''}\n${cell(p.value)}`,
        },
        upperLabel: { show: false },
        levels: [
          { itemStyle: { borderColor: theme.surface, borderWidth: 2, gapWidth: 2 } },
          { itemStyle: { borderColor: theme.surface, borderWidth: 1, gapWidth: 1 } },
        ],
      },
    ],
  };

  return { option };
};

export const Treemap = ({ data }: Props) => {
  const theme = useTheme();
  const view = useMemo(() => treemapView(data, theme), [data, theme]);
  if (!view) return <Empty message="Needs a label and a numeric column" />;
  return (
    <Plot>
      <EChart option={view.option} ariaLabel="Treemap" />
    </Plot>
  );
};
